/**
 * Pulsar — first-party web-analytics beacon (heimdall). UNLIKE the consent-gated trackers in this
 * folder (Google Analytics / Facebook Pixel / Reddit Pixel), Pulsar is our own first-party
 * telemetry and loads on EVERY page at boot, independent of the marketing-consent gate — it is
 * wired straight into `main.tsx`, not into `analytics-consent`.
 *
 * The beacon script is served by the configured Heimdall collector at `/static/js/pulsar/pulsar.js`;
 * it reads its own configuration off `document.currentScript`
 * (a classic `async` script, so `currentScript` resolves to our injected element during its
 * execution). We therefore inject a plain `<script>` carrying:
 *   - `src`           the collector-hosted beacon
 *   - `data-endpoint` where the beacon POSTs its batches
 *   - `data-domain`   the logical property domain — auto-filled from the LIVE host so every
 *                     whitelabel domain reports itself (mirrors how `front_url` is derived from
 *                     `window.location.hostname` in the Helm configmap)
 *   - `data-cookie-domain` the parent of an app. or lp. host, otherwise the exact host
 *   - `data-auth-cookie-scope` keeps authenticated ids host-only while anonymous ids stay shared
 *   - `data-global`   the beacon's global function name (pinned so identify()/logout() below stay
 *                     decoupled from the beacon's built-in default)
 * `data-client-id` / `data-site-id` are intentionally omitted.
 *
 * IDENTITY. The beacon holds the visitor's OWN ids (anonymous_user_id / anonymous_session_id) in
 * shared parent-domain cookies it mints itself. The HOST's authenticated ids are ours to supply: `user_id` and
 * `session_id` (opaque references — NEVER an auth token). We hand them over through the beacon's
 * global — `__ti('identify', { user_id, session_id })` — which writes them to the `_u` / `_s` host-only
 * cookies the beacon reads on every event; `__ti('logout')` clears them at sign-out. Calls made
 * before the async beacon has loaded are stashed on `window.<global>.q` and drained on load (the
 * standard pre-load queue pattern, same shape as gtag/fbq). See `components/pulsar-identity` for
 * the store→beacon wiring.
 *
 * Script and ingestion use the same tenant/environment-specific collector from
 * `window.config` (Helm-templated per tenant, like `googleAnalyticsId`). This keeps the beacon
 * transport aligned with Heimdall releases without a copied app asset. With no endpoint
 * configured Pulsar is disabled outright, exactly as a missing id disables the consent-gated
 * trackers — nothing is injected and no identity is queued.
 */

/** Where the beacon POSTs its batches — tenant/environment-specific, read from the ambient
 *  `window.config` (Helm-templated per tenant). Empty when unconfigured, which disables Pulsar. */
function pulsarEndpoint(): string {
  return window.config?.pulsarEndpoint ?? '';
}

// The beacon's global function name. Deliberately terse (camouflage) and matches the beacon's own
// default, but we set `data-global` to it explicitly so identify()/logout() never depend on that
// default staying put.
const PULSAR_GLOBAL = '__ti';

/**
 * Pulsar is OFF when there is no endpoint to report to (unconfigured tenant/env), or for the
 * prerender bot (whose hits would skew analytics and whose storage is disabled anyway — matching
 * the consent-gated trackers).
 */
function pulsarDisabled(): boolean {
  return Boolean(window.config?.inPrerender) || !pulsarEndpoint();
}

/** The beacon's global: a command function that also carries a pre-load queue on `.q`. */
type PulsarGlobal = ((command: string, payload?: unknown) => void) & { q?: unknown[][] };

/** The host-authenticated identity handed to the beacon. Opaque ids only — never an auth token. */
export interface PulsarIdentity {
  user_id?: string | null;
  session_id?: string | null;
}

// Injected once per page, at module scope so it survives React remounts (router rebuilds).
let injected = false;

function expireLegacyParentAuthCookies(cookieDomain: string): void {
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  for (const name of ['_u', '_s']) {
    try {
      document.cookie = `${name}=; path=/; max-age=0; samesite=lax; domain=${cookieDomain}${secure}`;
    } catch {
      // Blocked cookies must not prevent the application or beacon from starting.
    }
  }
}

/**
 * The beacon's global function, creating the pre-load queue stub if the async beacon has not
 * loaded yet. Calls stashed on `.q` are drained by the beacon on load.
 */
function pulsarGlobal(): PulsarGlobal {
  const w = window as unknown as Record<string, PulsarGlobal | undefined>;
  let fn = w[PULSAR_GLOBAL];
  if (typeof fn !== 'function') {
    const stub = function (this: unknown, ...args: unknown[]): void {
      (stub.q = stub.q ?? []).push(args);
    } as PulsarGlobal;
    stub.q = [];
    w[PULSAR_GLOBAL] = stub;
    fn = stub;
  }
  return fn;
}

/**
 * Inject the Pulsar beacon once. Idempotent, a no-op when Pulsar is disabled (no configured
 * endpoint, or the prerender bot — see `pulsarDisabled`).
 */
export function loadPulsar(): void {
  if (injected) return;
  if (pulsarDisabled()) return; // unconfigured tenant/env, or the prerender bot
  let scriptURL: string;
  try {
    const endpoint = new URL(pulsarEndpoint(), window.location.href);
    scriptURL = new URL('/static/js/pulsar/pulsar.js', endpoint).href;
  } catch {
    // Invalid telemetry configuration must not prevent the application from starting.
    return;
  }
  const cookieDomain = window.location.hostname.replace(/^(?:app|lp)\./, '');
  expireLegacyParentAuthCookies(cookieDomain);
  injected = true;

  pulsarGlobal(); // install the pre-load queue stub before the script can drain it

  const script = document.createElement('script');
  script.async = true;
  script.src = scriptURL;
  script.setAttribute('data-endpoint', pulsarEndpoint());
  // Auto-filled from the live host so every whitelabel domain reports itself.
  script.setAttribute('data-domain', window.location.hostname);
  script.setAttribute('data-cookie-domain', cookieDomain);
  script.setAttribute('data-auth-cookie-scope', 'host');
  script.setAttribute('data-global', PULSAR_GLOBAL);
  document.head.appendChild(script);
}

/**
 * Hand the host's authenticated identity to the beacon (persisted in the `_u` / `_s` host-only cookies).
 * Each id is written only when present, so a partial identity (e.g. a support-impersonation session
 * with no `session_id`) leaves the other cookie untouched. Pass opaque references only.
 */
export function pulsarIdentify(identity: PulsarIdentity): void {
  if (pulsarDisabled()) return;
  const payload: { user_id?: string; session_id?: string } = {};
  if (identity.user_id) payload.user_id = identity.user_id;
  if (identity.session_id) payload.session_id = identity.session_id;
  if (!payload.user_id && !payload.session_id) return;
  pulsarGlobal()('identify', payload);
}

/** Clear the host identity from the beacon (deletes the `_u` / `_s` cookies) at sign-out. */
export function pulsarLogout(): void {
  if (pulsarDisabled()) return;
  pulsarGlobal()('logout');
}

/**
 * Emit a custom Pulsar event. `name` is sent verbatim as the beacon's `event_type`; the beacon
 * auto-attaches the host identity (`_u` / `_s`), its own anonymous ids, device and page to every
 * event, so pass only opaque, non-PII `props`. No-op when Pulsar is disabled (no configured
 * endpoint, or the prerender bot), mirroring `pulsarIdentify` / `pulsarLogout`.
 *
 * `name` must NOT be `identify` / `logout` — those are the beacon's reserved commands, not events.
 * The funnel event_types this fires (generate_lead, begin_checkout, purchase, …, mirroring the GA4
 * names) never collide.
 */
export function pulsarEvent(name: string, props?: Record<string, unknown>): void {
  if (pulsarDisabled()) return;
  const ti = pulsarGlobal();
  props ? ti(name, props) : ti(name);
}
